export const MOCK_MODE_KEY = "redo.dev.mock_mode";
export const MOCK_RPC_OVERRIDES_KEY = "redo.dev.mock_rpc_overrides";

/**
 * Returns the mock query parameter value, or undefined if not present.
 * - `?mock=true` → "true" (generic mock mode)
 * - `?mock=<id>` → "<id>" (fixture-based mock mode)
 */
export function getMockParam(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    if (!window.location?.search) {
      return undefined;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get("mock") ?? undefined;
  } catch {
    return undefined;
  }
}

export function isMockModeEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const mockParam = getMockParam();
    if (mockParam) {
      localStorage.setItem(MOCK_MODE_KEY, "true");
      return true;
    }
    return localStorage.getItem(MOCK_MODE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Load a mock fixture by ID. Fetches `/__mock-data/<id>.json` and applies
 * the fixture data to localStorage mock keys.
 *
 * Fixture format:
 * ```json
 * {
 *   "rpcOverrides": { "getOrders": true },
 *   "mockDefault": true,
 *   "rpcData": { "getOrders": { ... } },
 *   "restData": { "GET /team": { ... } }
 * }
 * ```
 */
const MOCK_FIXTURE_ID_KEY = "redo.dev.mock_fixture_id";

export function getActiveFixtureId(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    return localStorage.getItem(MOCK_FIXTURE_ID_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export interface MockFixture {
  rpcOverrides?: Record<string, boolean>;
  mockDefault?: boolean;
  rpcData?: Record<string, unknown>;
  restData?: Record<string, unknown>;
}

/** Seed the mock localStorage keys from a parsed fixture. */
export function applyFixture(fixture: MockFixture): void {
  if (fixture.rpcOverrides) {
    localStorage.setItem(
      MOCK_RPC_OVERRIDES_KEY,
      JSON.stringify(fixture.rpcOverrides),
    );
  }
  if (fixture.mockDefault !== undefined) {
    localStorage.setItem(MOCK_DEFAULT_KEY, String(fixture.mockDefault));
  }
  if (fixture.rpcData) {
    localStorage.setItem(MOCK_DATA_KEY, JSON.stringify(fixture.rpcData));
  }
  if (fixture.restData) {
    localStorage.setItem(MOCK_REST_DATA_KEY, JSON.stringify(fixture.restData));
  }
}

/**
 * URL for a `.mock-data/<id>.json` file, resolved relative to the document's
 * base URI. Resolving against `document.baseURI` (rather than an absolute
 * `/__mock-data/...`) keeps the fetch correct when the app is served under a
 * deep prefix with a `<base href>` — e.g. an S3 mock preview at
 * `…/threads/<id>/merchant/latest/`. Locally `baseURI` is the origin root, so
 * this is identical to `/__mock-data/<id>.json`.
 */
export function mockDataUrl(id: string): string {
  // Encode the id so an id from a URL param or fixture file can't escape the
  // `__mock-data/` prefix (e.g. "../secret"). Valid ids (letters, digits, _, -)
  // pass through unchanged.
  const safeId = encodeURIComponent(id);
  if (typeof document === "undefined") {
    return `/__mock-data/${safeId}.json`;
  }
  return new URL(`__mock-data/${safeId}.json`, document.baseURI).toString();
}

export async function loadMockFixture(id: string): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const res = await fetch(mockDataUrl(id));
    if (!res.ok) {
      console.warn(`[mock] Failed to load fixture "${id}": ${res.status}`);
      return;
    }
    // Store fixture ID so reloads can re-fetch
    localStorage.setItem(MOCK_FIXTURE_ID_KEY, id);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    applyFixture((await res.json()) as MockFixture);
  } catch (e) {
    console.warn(`[mock] Error loading fixture "${id}":`, e);
  }
}

export function setMockModeEnabled(enabled: boolean): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (enabled) {
      localStorage.setItem(MOCK_MODE_KEY, "true");
    } else {
      localStorage.removeItem(MOCK_MODE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

export function getMockOverrides(): Record<string, boolean> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(MOCK_RPC_OVERRIDES_KEY);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function setMockOverride(name: string, enabled: boolean): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const overrides = getMockOverrides();
    overrides[name] = enabled;
    localStorage.setItem(MOCK_RPC_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore storage errors
  }
}

export function clearMockOverride(name: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const overrides = getMockOverrides();
    delete overrides[name];
    localStorage.setItem(MOCK_RPC_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore storage errors
  }
}

export function resetMockOverrides(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(MOCK_RPC_OVERRIDES_KEY);
  } catch {
    // ignore storage errors
  }
}

export const MOCK_DEFAULT_KEY = "redo.dev.mock_default";

/** Whether RPCs default to mocked when mock mode is on and no override exists. Defaults to true. */
export function getMockDefault(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    const raw = localStorage.getItem(MOCK_DEFAULT_KEY);
    return raw === null ? true : raw === "true";
  } catch {
    return true;
  }
}

export function setMockDefault(enabled: boolean): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(MOCK_DEFAULT_KEY, String(enabled));
  } catch {
    // ignore storage errors
  }
}

/** Returns true if this specific RPC call should be mocked. */
export function shouldMockRpc(name: string): boolean {
  if (!isMockModeEnabled()) {
    return false;
  }
  const overrides = getMockOverrides();
  if (name in overrides) {
    return overrides[name] ?? false;
  }
  return getMockDefault();
}

// ---- Known RPC registry (all RPCs ever seen, persisted to localStorage) ----

export const KNOWN_RPCS_KEY = "redo.dev.known_rpcs";

function loadKnownRpcs(): Set<string> {
  if (typeof window === "undefined") {
    return new Set();
  }
  try {
    const raw = localStorage.getItem(KNOWN_RPCS_KEY);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

const knownRpcs = loadKnownRpcs();

export function registerKnownRpcs(names: string[]): void {
  let changed = false;
  for (const name of names) {
    if (!knownRpcs.has(name)) {
      knownRpcs.add(name);
      changed = true;
    }
  }
  if (!changed) {
    return;
  }
  try {
    localStorage.setItem(KNOWN_RPCS_KEY, JSON.stringify(Array.from(knownRpcs)));
  } catch {
    // ignore storage errors
  }
}

export function getKnownRpcs(): string[] {
  return Array.from(knownRpcs).sort();
}

// ---- Called RPC tracking (persisted to localStorage) ----

export const CALLED_RPCS_KEY = "redo.dev.called_rpcs";

function loadCalledRpcs(): Set<string> {
  if (typeof window === "undefined") {
    return new Set();
  }
  try {
    const raw = localStorage.getItem(CALLED_RPCS_KEY);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

const calledRpcs = loadCalledRpcs();

export function trackRpcCall(name: string): void {
  if (calledRpcs.has(name)) {
    return;
  }
  calledRpcs.add(name);
  try {
    localStorage.setItem(
      CALLED_RPCS_KEY,
      JSON.stringify(Array.from(calledRpcs)),
    );
  } catch {
    // ignore storage errors
  }
}

export function getCalledRpcs(): string[] {
  return Array.from(calledRpcs).sort();
}

export function clearCalledRpcs(): void {
  calledRpcs.clear();
  try {
    localStorage.removeItem(CALLED_RPCS_KEY);
  } catch {
    // ignore storage errors
  }
}

// ---- Custom mock response data (user-edited JSON, persisted to localStorage) ----

export const MOCK_DATA_KEY = "redo.dev.mock_data";

const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function dateReviver(_key: string, value: unknown): unknown {
  if (typeof value === "string" && ISO_DATE_RE.test(value)) {
    return new Date(value);
  }
  return value;
}

function loadMockData(): Record<string, unknown> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(MOCK_DATA_KEY);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return raw ? (JSON.parse(raw, dateReviver) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function getMockResponse(name: string): unknown | undefined {
  return loadMockData()[name];
}

export function setMockResponse(name: string, value: unknown): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const data = loadMockData();
    data[name] = value;
    localStorage.setItem(MOCK_DATA_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}

export function clearMockResponse(name: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const data = loadMockData();
    delete data[name];
    localStorage.setItem(MOCK_DATA_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}

// ---- REST endpoint override data (user-edited JSON, persisted to localStorage) ----

export const MOCK_REST_DATA_KEY = "redo.dev.mock_rest_data";

function loadMockRestData(): Record<string, unknown> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(MOCK_REST_DATA_KEY);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function getMockRestResponse(pattern: string): unknown | undefined {
  return loadMockRestData()[pattern];
}

export function setMockRestResponse(pattern: string, value: unknown): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const data = loadMockRestData();
    data[pattern] = value;
    localStorage.setItem(MOCK_REST_DATA_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}

export function clearMockRestResponse(pattern: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const data = loadMockRestData();
    delete data[pattern];
    localStorage.setItem(MOCK_REST_DATA_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}
